import dayjs from "dayjs";
import React, { useMemo } from "react"
import styled, { useTheme } from "styled-components";

import IconCheck from "@floji-internal/mui-icons/Check";
import IconMoreHoriz from '@floji-internal/mui-icons/MoreHoriz';

import { CemTimedAction } from "--root/static_types/cem.types";

import { getReadableDateTime, getReadableRelativeTime } from "--root/utils/datetime";
import { groupByMap } from "--root/utils/groupBy";

import { Body1, Body3 } from "--components/typography";
import { ColorSet } from "--components/theme";

import { Timeline, TimelineHeader, TimelineEntry, TimelineProps } from "--floponents-web/atoms/Timeline/Timeline";

import { cemActionLabelMap, getCemActionColorMap } from "./utils";

const timeFormatter = new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "numeric"
});

const ActionTitleName = styled(Body1)`
    display: inline-block;
    line-height: inherit;
`;
const PreviousActionTitleName = styled(ActionTitleName)`
    color: ${props => props.theme.palette.text.secondary};
`;
const CurrentActionTitleName = styled(ActionTitleName) <{ actionColor: ColorSet }>`
    color: ${props => props.actionColor.lighter};
    font-size: 1.15rem;
    font-weight: ${props => props.theme.typography.bold};
`;
const ActionTitleRelativeTime = styled(Body3)`
    display: inline-block;
`;

export interface CemTimelineProps extends TimelineProps {
    actions: CemTimedAction[];
    showProgress?: boolean;
    suspendedAt?: number;
}
export const CemTimeline = (props: CemTimelineProps) => {
    const { actions, showProgress, suspendedAt, ...timelineProps } = props;

    const theme = useTheme();

    const actionsByDay = useMemo(() => (
        groupByMap(actions, action => (
            dayjs(action.timestamp).startOf("day")
        ))
    ), [actions]);

    const now = suspendedAt ?? Date.now();
    const currentAction = actions.find(action => action.timestamp > now)

    return (
        <Timeline {...timelineProps}>
            {[...actionsByDay.entries()].map(([date, dayActions], index, arr) => {
                const prevDate = arr[index - 1]?.[0];

                const dayMarkerFormatOpts: Intl.DateTimeFormatOptions = {};
                if (!showProgress || !date.isSame(now, "date")) {
                    dayMarkerFormatOpts.weekday = "short";
                }
                // Add additional date info as necessary
                if (!date.isSame(now, "isoWeek")) {
                    dayMarkerFormatOpts.day = "numeric";

                    if (!date.isSame(now, "month")) {
                        dayMarkerFormatOpts.month = "short";
                    }
                    if (!date.isSame(now, "year")) {
                        dayMarkerFormatOpts.year = "numeric";
                    }
                }

                if (showProgress) {
                    dayMarkerFormatOpts.hour = "numeric";
                    dayMarkerFormatOpts.minute = "numeric";
                }

                const dayMarkerFormatter = new Intl.DateTimeFormat(
                    undefined,
                    dayMarkerFormatOpts
                );

                return <>
                    {!showProgress && (!prevDate || !date.isSame(prevDate, "day")) && (
                        <TimelineHeader>
                            {getReadableDateTime(date.toDate(), dayMarkerFormatter)}
                        </TimelineHeader>
                    )}

                    {dayActions.map(action => {
                        const actionColor = getCemActionColorMap(theme)[action.type];
                        const IndicatorIcon = showProgress
                            ? action.timestamp < now
                                ? IconCheck
                                : action === currentAction
                                    ? IconMoreHoriz
                                    : undefined
                            : undefined;

                        const actionType = cemActionLabelMap[action.type];

                        // If not suspended or action has already run, we have a time to display
                        const hasKnownTime =
                            props.suspendedAt === undefined || action.timestamp < now;

                        return (
                            // eslint-disable-next-line react/jsx-key
                            <TimelineEntry
                                title={
                                    showProgress
                                        ? <>
                                            {action === currentAction
                                                ? <CurrentActionTitleName actionColor={actionColor}>
                                                    {actionType}
                                                </CurrentActionTitleName>
                                                : action.timestamp < now
                                                    ? <PreviousActionTitleName>
                                                        {actionType}
                                                    </PreviousActionTitleName>
                                                    : <ActionTitleName>
                                                        {actionType}
                                                    </ActionTitleName>}
                                            {" "}
                                            {hasKnownTime && (
                                                <ActionTitleRelativeTime>
                                                    {getReadableRelativeTime(action.timestamp, now)}
                                                </ActionTitleRelativeTime>
                                            )}
                                        </>
                                        : cemActionLabelMap[action.type]
                                }
                                label={
                                    hasKnownTime
                                        ? getReadableDateTime(
                                            action.timestamp,
                                            showProgress ? dayMarkerFormatter : timeFormatter
                                        )
                                        : undefined
                                }
                                indicatorColor={actionColor.main}
                                indicatorIcon={
                                    IndicatorIcon
                                        ? <IndicatorIcon
                                            size={showProgress && action === currentAction
                                                ? "18px"
                                                : "14px"}
                                            color={actionColor.contrast}
                                        />
                                        : undefined
                                }
                            />
                        );
                    })}
                </>;
            })}
        </Timeline>
    );
};
